import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Link from '@material-ui/core/Link';
import Chart from './Chart';
import Deposits from './Deposits';
import Orders from './Orders';
import { RadarGraph } from './Radar';
import { BarChartDash } from './BarChart';
import { doc, onSnapshot } from 'firebase/firestore';
import {
	DIVISIONS,
	MONTHS,
	STATUS,
	TYPES_OF_PACKING,
	YEARS,
} from '../../utils/lists';
import { db } from '../../../firebase/firebase-config';
import { get5LatestOrders, updateStats } from '../../../actions/orders';
import { formatDateMX, getCurrentMonth } from '../../../helpers/hours';
import { getFullName, money } from '../../../helpers/utils';
import { SelectComponent } from '../../orders/SummaryResults';
import {
	Autocomplete,
	Button,
	FormControlLabel,
	FormGroup,
	Switch,
} from '@mui/material';
import { getDashboardReport } from '../../../actions/dashboard';
import TableTemplate from './TableTemplate';

function Copyright() {
	return (
		<Typography variant='body2' color='textSecondary' align='center'>
			<Link
				color='inherit'
				href='https://datagram-software.vercel.app/'
				target='_blank'
			>
				Datagram
			</Link>{' '}
			{' - '}
			<Link
				color='inherit'
				href='https://piluniformes.com.mx/'
				target='_blank'
			>
				Pilu
			</Link>{' '}
			{new Date().getFullYear()}.
		</Typography>
	);
}

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex',
		width: '100%',
	},
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	paper: {
		padding: theme.spacing(2),
		display: 'flex',
		overflow: 'none',
		flexDirection: 'column',
	},
	fixedHeight: {
		height: 240,
	},
	fixedHeightIndicator: {
		height: 150,
	},
	fixedHeightMedium: {
		height: 285,
	},
	fixedHeightMedium2: {
		height: 320,
	},
	fixedHeightLarger: {
		height: 500,
	},
}));

const ordersColumns = [
	'Fecha',
	'Número de orden',
	'Creado por',
	'Importe',
	'Cliente',
	'Tipo de empaque',
];

const getMonthNumber = (month) => {
	// Get the month number
	return month
		? MONTHS.find(({ name }) => month === name).number
		: new Date().getMonth() + 1;
};

export default function Dashboard() {
	// States
	const [stats, setStats] = useState({
		radar: [],
		indicators: [],
		amounts: [],
		amounts_authorize: [],
		orders: [],
		counts: [],
	});

	// Profits (all statuses | authorized)
	const [profitAuthorized, setProfitAuthorized] = useState(false);

	// Filter states
	const [month, setMonth] = useState(getCurrentMonth());
	const [year, setYear] = useState(new Date().getFullYear().toString());

	const classes = useStyles();

	const fixedHeightPaperIndicator = clsx(
		classes.paper,
		classes.fixedHeightIndicator
	);

	const fixedHeightPaperMedium = clsx(
		classes.paper,
		classes.fixedHeightMedium
	);

	const fixedHeightPaperMedium2 = clsx(
		classes.paper,
		classes.fixedHeightMedium2
	);

	const fixedHeightPaperLarger = clsx(
		classes.paper,
		classes.fixedHeightLarger
	);

	const handleDownloadExcelReport = async () => {
		await getDashboardReport(getMonthNumber(month));
	};

	useEffect(() => {
		const getRadarData = (newStats) => {
			const data = DIVISIONS.map(({ name }) => ({
				subject: name,
				number: newStats[name],
			}));
			const fullMark = Math.max(...data.map(({ number }) => number));
			return data.map((item) => ({
				...item,
				fullMark,
			}));
		};

		const getIndicatorsData = (newStats) => {
			return STATUS.map(({ name, label }) => ({
				name: label,
				code: name,
				number: newStats[name],
				currentMonth: newStats[`${name}_CURRENT`],
			}));
		};

		const getAmountsData = (newStats) => {
			return DIVISIONS.map(({ name }) => ({
				name,
				importe: newStats[`${name}_AMOUNT`],
			}));
		};

		const getAmountsAuthorizedData = (newStats) => {
			return DIVISIONS.map(({ name }) => ({
				name,
				importe: newStats[`${name}_AMOUNT_AUT`],
			}));
		};

		const getCountsData = (newStats) => {
			return DIVISIONS.map(({ name }, index) => ({
				id: index + 1,
				code: name,
				Anterior: newStats[`${name}_COUNT_LAST`],
				Actual: newStats[`${name}_COUNT_CURRENT`],
			}));
		};

		const formatOrders = (newOrders) => {
			return newOrders?.map((item) => ({
				col1: formatDateMX(item.created_at),
				col2: {
					id: item.id,
					name: item.number_order,
				},
				col3: getFullName(item.created_by),
				col4: money(item.amount),
				col5: item.client_id.company_name,
				col6:
					TYPES_OF_PACKING.find(
						(item2) => item2.name === item.type_of_packing
					)?.label || 'No asignado',
			}));
		};

		// Stats listener
		const statsDocRef = doc(db, 'stats', 'stats');

		// Listeners for stats info in Firestore
		onSnapshot(statsDocRef, (snapshot) => {
			const statsResponse = snapshot.data();

			// Set data
			setStats((old) => ({
				...old,
				indicators: getIndicatorsData(statsResponse),
				radar: getRadarData(statsResponse),
				amounts: getAmountsData(statsResponse),
				amounts_authorize: getAmountsAuthorizedData(statsResponse),
				counts: getCountsData(statsResponse),
				global_counter: statsResponse['ORDERS_GLOBAL_COUNT_CURRENT'],
				current_month_name: statsResponse?.current_month_name,
				last_month_name: statsResponse?.last_month_name,
				type_summary_list: statsResponse?.type_summary_list,
				state_summary_list: statsResponse?.state_summary_list,
			}));
		});

		// Get last 5 orders
		get5LatestOrders().then(({ data }) => {
			setStats((old) => ({
				...old,
				orders: formatOrders(data),
			}));
		});
		return;
	}, []);

	useEffect(() => {
		// Get the month number
		const montNumber = getMonthNumber(month);
		// Update latest statistics
		updateStats(montNumber, year).catch((error) => console.log(error));
	}, [month, year]);

	return (
		<div className={classes.root}>
			{stats && (
				<main>
					<Container maxWidth='lg' className={classes.container}>
						<Grid container spacing={3}>
							{/* Title for indicators */}
							<Grid item xs={6}>
								<Typography
									variant='h4'
									color='textSecondary'
									align='left'
								>
									Indicadores de pedidos
								</Typography>
								<small>
									({stats?.global_counter} pedido(s) en{' '}
									{stats?.current_month_name ?? '-'} {year})
								</small>
							</Grid>

							{/* Month */}
							<Grid item xs={2}>
								<Autocomplete
									required
									style={{ marginTop: '-17px' }}
									fullWidth
									value={month}
									onChange={(_, value) => {
										setMonth(value);
									}}
									options={MONTHS.map((item) => item.name)}
									renderInput={(params) =>
										SelectComponent(params, 'Mes')
									}
								/>
							</Grid>

							{/* Month */}
							<Grid item xs={2}>
								<Autocomplete
									required
									style={{ marginTop: '-17px' }}
									fullWidth
									value={year}
									onChange={(_, value) => {
										setYear(value);
									}}
									options={YEARS}
									renderInput={(params) =>
										SelectComponent(params, 'Año')
									}
								/>
							</Grid>

							{/* Excel data download */}
							<Grid item xs={2}>
								<Button
									variant='contained'
									color='success'
									onClick={handleDownloadExcelReport}
								>
									Descargar Excel
								</Button>
							</Grid>

							{/* Indicators */}
							{stats?.indicators.map(
								(
									{ name, number, code, currentMonth },
									index
								) => (
									<Grid item xs={3} key={index}>
										<Paper
											className={
												fixedHeightPaperIndicator
											}
										>
											<Deposits
												name={name}
												number={number}
												icon={code}
												monthly={currentMonth}
											/>
										</Paper>
									</Grid>
								)
							)}

							{/* -------------------- */}

							{/* Title for graphs */}
							<Grid item xs={12}>
								<Typography
									variant='h4'
									color='textSecondary'
									align='left'
								>
									Divisiones
								</Typography>
							</Grid>

							{/* Graph */}
							<Grid item xs={5}>
								<Paper className={fixedHeightPaperLarger}>
									<RadarGraph divisions={stats?.radar} />
								</Paper>
							</Grid>

							{/* Chart */}
							<Grid item xs={7}>
								<Grid container>
									<Grid item xs={12}>
										<Paper
											className={fixedHeightPaperMedium2}
										>
											<div
												style={{ margin: '0 0 10px 0' }}
											>
												<FormGroup>
													<FormControlLabel
														control={
															<Switch
																checked={
																	profitAuthorized
																}
																onChange={() =>
																	setProfitAuthorized(
																		!profitAuthorized
																	)
																}
															/>
														}
														label='Mostrar solo autorizados'
													/>
												</FormGroup>
											</div>
											<Chart
												data={
													profitAuthorized
														? stats?.amounts_authorize
														: stats?.amounts
												}
												month={
													stats?.current_month_name
												}
											/>
										</Paper>
									</Grid>
									<Grid
										item
										xs={12}
										style={{ margin: '20px 0 0 0' }}
									>
										<Paper
											className={fixedHeightPaperMedium}
										>
											<BarChartDash
												data={stats?.counts}
												currentMonth={
													stats?.current_month_name
												}
												lastMonth={
													stats?.last_month_name
												}
											/>
										</Paper>
									</Grid>
								</Grid>
							</Grid>

							{/* -------------------- */}

							{/* Title for graphs */}
							<Grid item xs={12}>
								<Typography
									variant='h4'
									color='textSecondary'
									align='left'
								>
									Acciones más recientes
								</Typography>
							</Grid>
							{/* Recent Orders */}
							<Grid item xs={12}>
								<Paper className={classes.paper}>
									<Orders
										name='Pedidos'
										link
										columns={ordersColumns}
										rows={stats?.orders}
									/>
								</Paper>
							</Grid>
							{/* Title for summary */}
							<Grid item xs={12}>
								<Typography
									variant='h4'
									color='textSecondary'
									align='left'
								>
									Resumen de los clientes
								</Typography>
							</Grid>
							{/* Summary clients */}
							<Grid item xs={12}>
								<Paper className={classes.paper}>
									<TableTemplate
										name='Resumen de los clientes'
										columns={['Estado', 'Cantidad']}
										keys={['state', 'state_count']}
										rows={stats?.state_summary_list}
									/>
								</Paper>
							</Grid>
						</Grid>
						<Box pt={4}>
							<Copyright />
						</Box>
					</Container>
				</main>
			)}
		</div>
	);
}
